// components/Navbar.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  alpha,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  Dashboard as DashboardIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  BrightnessAuto as AutoModeIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const { currentUser, isAdmin, isSuperAdmin, logout } = useAuth();
  const { mode, actualMode, toggleColorMode, primaryColor, setPrimaryColor } = useCustomTheme();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'About', path: '/about', icon: <InfoIcon /> },
    { text: 'Contact', path: '/contact', icon: <ContactIcon /> },
  ];

  const themeOptions = [
    { mode: 'light', icon: <LightModeIcon />, label: 'Light Mode' },
    { mode: 'dark', icon: <DarkModeIcon />, label: 'Dark Mode' },
    { mode: 'system', icon: <AutoModeIcon />, label: 'System Default' },
  ];

  const colorOptions = [
    { color: '#1976d2', label: 'Default Blue' },
    { color: '#9c27b0', label: 'Purple' },
    { color: '#ed6c02', label: 'Orange' },
    { color: '#2e7d32', label: 'Green' },
    { color: '#d32f2f', label: 'Red' },
    { color: '#0288d1', label: 'Cyan' },
  ];

  const drawer = (
    <Box sx={{ width: 280, p: 2, height: '100%', bgcolor: theme.palette.background.paper }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: theme.palette.text.secondary }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              handleDrawerToggle();
            }}
            sx={{
              borderRadius: 2,
              mb: 1,
              color: theme.palette.text.primary,
              '&:hover': { 
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                transform: 'translateX(4px)',
              },
              transition: 'all 0.2s',
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Theme Section in Mobile Drawer */}
      <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1, px: 2 }}>
        Appearance
      </Typography>
      <List>
        {themeOptions.map((option) => (
          <ListItem
            button
            key={option.mode}
            onClick={() => {
              toggleColorMode(option.mode);
            }}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              bgcolor: mode === option.mode ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
            }}
          >
            <ListItemIcon sx={{ color: mode === option.mode ? theme.palette.primary.main : theme.palette.text.secondary, minWidth: 40 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText 
              primary={option.label} 
              primaryTypographyProps={{ 
                sx: { 
                  color: mode === option.mode ? theme.palette.primary.main : theme.palette.text.primary,
                  fontWeight: mode === option.mode ? 600 : 400,
                } 
              }}
            />
            {mode === option.mode && <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1, px: 2 }}>
        Primary Color
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, mb: 2 }}>
        {colorOptions.map((option) => (
          <Tooltip title={option.label} key={option.color}>
            <Box
              onClick={() => setPrimaryColor(option.color)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: option.color,
                cursor: 'pointer',
                border: primaryColor === option.color ? `3px solid ${theme.palette.common.white}` : 'none',
                outline: primaryColor === option.color ? `2px solid ${option.color}` : 'none',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <List>
        {currentUser ? (
          <>
            {isAdmin && (
              <ListItem
                button
                onClick={() => {
                  navigate('/admin/dashboard');
                  handleDrawerToggle();
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  color: theme.palette.text.primary,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItem>
            )}
            <ListItem
              button
              onClick={() => {
                navigate('/admin/profile');
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.info.main, minWidth: 40 }}>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate('/settings');
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.warning.main, minWidth: 40 }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem
              button
              onClick={() => {
                handleLogout();
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                color: theme.palette.error.main,
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              button
              onClick={() => {
                navigate('/auth/sign-in');
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Sign In" />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate('/auth/sign-up');
                handleDrawerToggle();
              }}
              sx={{
                borderRadius: 2,
                mb: 1,
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.secondary.main, minWidth: 40 }}>
                <PersonAddIcon />
              </ListItemIcon>
              <ListItemText primary="Sign Up" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  const getThemeIcon = () => {
    if (actualMode === 'light') return <LightModeIcon />;
    if (actualMode === 'dark') return <DarkModeIcon />;
    return <AutoModeIcon />;
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0, sm: 2 }, py: 1 }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px',
                }}
              >
                AediaX
              </Typography>
              {isSuperAdmin && (
                <Chip
                  label="Super Admin"
                  size="small"
                  sx={{
                    ml: 1,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              {isAdmin && !isSuperAdmin && (
                <Chip
                  label="Admin"
                  size="small"
                  sx={{
                    ml: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.text}
                    onClick={() => navigate(item.path)}
                    sx={{
                      color: theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s',
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2,
                      px: 2,
                    }}
                  >
                    {item.text}
                  </Button>
                ))}

                {/* Theme Toggle Button */}
                <Tooltip title="Theme Settings">
                  <IconButton
                    onClick={handleThemeMenuOpen}
                    sx={{
                      color: theme.palette.text.primary,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'rotate(15deg)',
                      },
                    }}
                  >
                    {getThemeIcon()}
                  </IconButton>
                </Tooltip>

                {/* Theme Settings Menu */}
                <Menu
                  anchorEl={themeMenuAnchor}
                  open={Boolean(themeMenuAnchor)}
                  onClose={handleThemeMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      width: 280,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      boxShadow: theme.shadows[4],
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1,
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: theme.palette.text.secondary }}>
                    Theme Mode
                  </Typography>
                  {themeOptions.map((option) => (
                    <MenuItem
                      key={option.mode}
                      onClick={() => {
                        toggleColorMode(option.mode);
                        handleThemeMenuClose();
                      }}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.5,
                        bgcolor: mode === option.mode ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      }}
                    >
                      <Box sx={{ mr: 1.5, color: mode === option.mode ? theme.palette.primary.main : theme.palette.text.secondary }}>
                        {option.icon}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: mode === option.mode ? 600 : 400 }}>
                        {option.label}
                      </Typography>
                      {mode === option.mode && <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />}
                    </MenuItem>
                  ))}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: theme.palette.text.secondary }}>
                    Primary Color
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, py: 1 }}>
                    {colorOptions.map((option) => (
                      <Tooltip title={option.label} key={option.color}>
                        <Box
                          onClick={() => {
                            setPrimaryColor(option.color);
                            handleThemeMenuClose();
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: option.color,
                            cursor: 'pointer',
                            border: primaryColor === option.color ? `2px solid ${theme.palette.common.white}` : 'none',
                            outline: primaryColor === option.color ? `2px solid ${option.color}` : 'none',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Menu>

                {currentUser ? (
                  <>
                    <IconButton onClick={handleMenuOpen} sx={{ p: 0, ml: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 40,
                          height: 40,
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        {currentUser.email?.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                    
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 200,
                          borderRadius: 2,
                          bgcolor: theme.palette.background.paper,
                          boxShadow: theme.shadows[4],
                          border: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <MenuItem 
                        onClick={() => {
                          navigate('/admin/profile');
                          handleMenuClose();
                        }}
                        sx={{ gap: 1.5, py: 1 }}
                      >
                        <PersonIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                        Profile
                      </MenuItem>
                      <MenuItem 
                        onClick={() => {
                          navigate('/settings');
                          handleMenuClose();
                        }}
                        sx={{ gap: 1.5, py: 1 }}
                      >
                        <SettingsIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                        Settings
                      </MenuItem>
                      <Divider />
                      <MenuItem 
                        onClick={handleLogout} 
                        sx={{ 
                          gap: 1.5, 
                          py: 1,
                          color: theme.palette.error.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        <LogoutIcon fontSize="small" />
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/auth/sign-in')}
                      startIcon={<LoginIcon />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/auth/sign-up')}
                      startIcon={<PersonAddIcon />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        bgcolor: theme.palette.primary.main,
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          bgcolor: theme.palette.primary.dark,
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      Sign Up
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Theme">
                  <IconButton
                    onClick={handleThemeMenuOpen}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {getThemeIcon()}
                  </IconButton>
                </Tooltip>
                <IconButton onClick={handleDrawerToggle} sx={{ color: theme.palette.text.primary }}>
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Theme Menu for Mobile */}
      <Menu
        anchorEl={themeMenuAnchor}
        open={Boolean(themeMenuAnchor)}
        onClose={handleThemeMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 280,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
            p: 1,
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: theme.palette.text.secondary }}>
          Theme Mode
        </Typography>
        {themeOptions.map((option) => (
          <MenuItem
            key={option.mode}
            onClick={() => {
              toggleColorMode(option.mode);
              handleThemeMenuClose();
            }}
            sx={{
              borderRadius: 1.5,
              mb: 0.5,
              bgcolor: mode === option.mode ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            }}
          >
            <Box sx={{ mr: 1.5, color: mode === option.mode ? theme.palette.primary.main : theme.palette.text.secondary }}>
              {option.icon}
            </Box>
            <Typography variant="body2" sx={{ flex: 1, fontWeight: mode === option.mode ? 600 : 400 }}>
              {option.label}
            </Typography>
            {mode === option.mode && <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />}
          </MenuItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: theme.palette.text.secondary }}>
          Primary Color
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, py: 1 }}>
          {colorOptions.map((option) => (
            <Tooltip title={option.label} key={option.color}>
              <Box
                onClick={() => {
                  setPrimaryColor(option.color);
                  handleThemeMenuClose();
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: option.color,
                  cursor: 'pointer',
                  border: primaryColor === option.color ? `2px solid ${theme.palette.common.white}` : 'none',
                  outline: primaryColor === option.color ? `2px solid ${option.color}` : 'none',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;